import React, { useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Grid,
  Tab,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useTranslation } from "react-i18next";
import Div from "@jumbo/shared/Div/Div";
import * as Yup from "yup";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import {
  BasicSettingsTab,
  LicenseTab,
  RelationsTab,
} from "./SmartkioskCustomerFields/tabs";

const FIELD_NAME = "DSOK_CUSTOMER_FIELD";
export const DSOK_CUSTOMER_FIELD_SCHEMA = {
  storeCode: "",
  customerId: "",
  code: "",
  languages: [],
  defaultLanguage: "",
  displayLanguageSwitcher: "",
  servicePartnerId: 0,
  posIntegration: "",
  communicationType: "ReverseSSH",
  license: {
    type: "basic",
  },
  customerRelation: {
    customerType: "standalone",
    parentCustomerId: null,
    relationshipType: null,
  },
  // DSOK-442: externé objednávky (POST /api/v1/orders/external). enabled = hlavný
  // prepínač. Autentifikácia je Bearer JWT (verejné API v1). Uložené v dsokSettings.customer.
  externalOrders: {
    enabled: false,
  },
  // DSOK-475: QR objednávanie zo stola. isActive = hlavný prepínač (sprístupní sekciu +
  // mapu stolov + QR API). Besteron tajomstvá šifruje backend (PUT /api/v1/qr/besteron),
  // ukladá sa len šifrovaný blob. Uložené v dsokSettings.customer.qrOrders.
  qrOrders: {
    isActive: false,
    enabled: false,
    slug: "",
    logo: "",
    orderExpiryMinutes: 15,
    // Show an order-level „poznámka do kuchyne" (note to kitchen) field on the QR web. Opt-in.
    kitchenNote: false,
    // Delivery/fulfillment modes offered on the QR web: table / pickup / delivery.
    usage: ["table"],
    payments: { online: true, staff: true, minOnlineAmount: 0, staffMethods: ["cash", "card"] },
    // Receipt delivery per payment type (online / staff); legacy flat array still accepted by backend.
    fiscal: { delivery: { online: ["print", "email"], staff: ["print", "email"] }, printWhenEmail: false },
    auth: { email: true, google: true, facebook: true },
    callStaff: { waiter: false, bill: false },
    besteron: { cid: "", environment: "sandbox" },
  },
  // F2 WEB objednávanie (e-shop režim tej istej aplikácie). Gated na qrOrders.enabled. Doména (host
  // mapovanie) sa ukladá zvlášť cez /api/v1/qr/web/domains (Mongo), nie tu. Uložené v
  // dsokSettings.customer.web.
  web: {
    enabled: false,
    modes: { dine: false, take: true, delivery: false },
    menuAssign: { dineIn: null, takeaway: null, delivery: null },
    // Delivery fee + min order live in „QR objednávanie → Nastavenia → Web nastavenia"
    // (dsokQrHighlights.web.delivery), not here.
    // Per-mode payment (online / pay-at-handover) + online receipt options (e-mail / physical) for web.
    modeConfig: {
      delivery: { payOnline: true, payHandover: true, onlineReceipt: { email: true, physical: true } },
      take: { payOnline: true, payHandover: true, onlineReceipt: { email: true, physical: true } },
      dine: { payOnline: true, payHandover: true, onlineReceipt: { email: true, physical: true } },
    },
    // Opening hours + fulfillment time live in „QR objednávanie → Nastavenia → Web nastavenia"
    // (dsokQrHighlights.web), not here.
    indexing: { enabled: false, seo: { title: "", description: "", ogImage: "" } },
  },
};

export const getDsokCustomerFieldValidationSchema = (t) => ({
  storeCode: Yup.string().required(t("messages.checkRequiredFields")),
  customerId: Yup.number().required(t("messages.checkRequiredFields")),
  code: Yup.string().required(t("messages.checkRequiredFields")),
  languages: Yup.array()
    .min(1, t("messages.checkRequiredFields"))
    .required(t("messages.checkRequiredFields")),
  defaultLanguage: Yup.string().required(t("messages.checkRequiredFields")),
});

const SmartkioskCustomerFields = ({
  handleChange,
  expanded,
  isPartnerAdmin,
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("basicSettings");

  return (
    <Accordion
      expanded={expanded === FIELD_NAME}
      onChange={handleChange(FIELD_NAME)}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography sx={{ width: "33%", flexShrink: 0 }}>
          {t("pages.dsokDetails.customerSettings")}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={2}>
          <Grid item xs={12} md={12}>
            <TabContext value={activeTab}>
              <Div sx={{ borderBottom: 1, borderColor: "divider" }}>
                <TabList onChange={(_, newTab) => setActiveTab(newTab)}>
                  <Tab
                    label={t(
                      "pages.dsokDetails.customerSettingsTabs.tabs.basicSettings",
                    )}
                    value={"basicSettings"}
                  />
                  <Tab
                    label={t(
                      "pages.dsokDetails.customerSettingsTabs.tabs.license",
                    )}
                    value={"license"}
                  />
                  <Tab
                    label={t(
                      "pages.dsokDetails.customerSettingsTabs.tabs.relations",
                    )}
                    value={"relations"}
                  />
                </TabList>
              </Div>

              <TabPanel value={"basicSettings"} sx={{ p: 0, m: 0 }}>
                <Grid
                  item
                  xs={12}
                  md={12}
                  lg={12}
                  container
                  spacing={2}
                  sx={{ p: 0, m: 0 }}
                >
                  <BasicSettingsTab isPartnerAdmin={isPartnerAdmin} />
                </Grid>
              </TabPanel>

              <TabPanel value={"license"} sx={{ p: 0, m: 0 }}>
                <Grid
                  item
                  xs={12}
                  md={12}
                  lg={12}
                  container
                  spacing={2}
                  sx={{ p: 0, m: 0 }}
                >
                  <Grid item xs={12}>
                    <LicenseTab />
                  </Grid>
                </Grid>
              </TabPanel>

              <TabPanel value={"relations"} sx={{ p: 0, m: 0 }}>
                <Grid
                  item
                  xs={12}
                  md={12}
                  lg={12}
                  container
                  spacing={2}
                  sx={{ p: 0, m: 0 }}
                >
                  <Grid item xs={12}>
                    <RelationsTab />
                  </Grid>
                </Grid>
              </TabPanel>
            </TabContext>
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};

export default SmartkioskCustomerFields;
